/**
 * Turn a report filter's URL values into ids the API accepts.
 *
 * Until the department/team model, report links carried department NAMES (`?rdept=Sales`).
 * The API now takes uuids only and answers a name with 422, so an old bookmark or drill-down
 * must be resolved against the filter options first. Serves departments and teams alike.
 *
 * - A value equal to an option id is kept; a value equal to an option NAME becomes its id;
 *   anything else is dropped.
 * - Before the options arrive, uuid-shaped values pass through, but any name makes the result
 *   `pending` with no ids: the caller must wait rather than send the name or send nothing
 *   (which would show organisation-wide numbers under a department chip).
 *
 * Pure; no React or service imports, so it stays testable under Jest.
 */

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** True when `value` has the shape of a uuid (the only thing the report API accepts). */
export const isUuid = (value: string): boolean => UUID.test(value);

export function resolveIdSelection(
  raw: string[],
  options: Array<{ id: string; name: string }> | undefined
): { ids: string[]; pending: boolean } {
  if (!options) {
    return raw.every(isUuid) ? { ids: raw, pending: false } : { ids: [], pending: true };
  }

  const ids: string[] = [];
  for (const value of raw) {
    const match = options.find((o) => o.id === value) ?? options.find((o) => o.name === value);
    if (match && !ids.includes(match.id)) ids.push(match.id);
  }
  return { ids, pending: false };
}
